import { Box, Typography } from '@mui/material';
import { Download } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { createAdminApi, type AdminApi, type AdminExportFile } from '@/admin/adminApi';
import { saveFile } from '@/admin/audit/auditExport';
import { ReadBlock } from '@/admin/pilotage/ReadBlock';
import { useAdminRead } from '@/admin/pilotage/useAdminRead';
import { EXPORT_LIMITATION, EXPORT_NOT_A_COMPLETE_ANSWER } from '@/admin/privacy/exportLimitation';
import { formatInstant } from '@/admin/users/accountPresentation';
import { messageOf } from '@/admin/users/adminErrors';
import { useHttpClient } from '@/app/providers';
import { Badge, Button, Card } from '@/ui/kit';

import { InfoNote } from './InfoNote';

/** Les cinq derniers suffisent : la liste complete se lit sur la fiche d'un compte. */
const RECENT_EXPORTS = 5;

const readExports = (api: AdminApi) => api.listExports(1, RECENT_EXPORTS);

/**
 * « Exports produits (validite 15 min) ».
 *
 * Trois choses sont dites, parce que la maquette en disait de fausses :
 * - **ce n'est pas un lien signe** : le fichier se telecharge par la session de
 *   l'administrateur (jeton Bearer), et **ce telechargement est audite** ;
 * - **la fenetre est de 15 minutes**, l'etat « Valide » ou « Expire » est celui du
 *   serveur — l'ecran ne le recalcule pas ;
 * - **un export n'est jamais une reponse complete** (#527) : la limite est dite
 *   avant tout geste, dans l'encart du haut.
 *
 * Le telechargement est reserve a `admin` ; un `support` lit la liste et l'ecran
 * le dit plutot que de griser un bouton sans explication.
 */
export const ExportsCard = ({
  refreshToken,
  canDownload,
}: {
  readonly refreshToken: number;
  readonly canDownload: boolean;
}): ReactElement => {
  const http = useHttpClient();
  const { state, reload } = useAdminRead(readExports, refreshToken);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ readonly kind: 'ok' | 'error'; readonly message: string } | null>(null);

  const download = async (file: AdminExportFile): Promise<void> => {
    setDownloading(file.id);
    setOutcome(null);

    try {
      const saved = saveFile(await createAdminApi(http).downloadExport(file.id));
      setOutcome({ kind: 'ok', message: `« ${saved} » téléchargé. Ce téléchargement est enregistré au journal d’audit.` });
    } catch (error: unknown) {
      setOutcome({ kind: 'error', message: messageOf(error) });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Card title="Exports produits (validité 15 min)" subtitle="Téléchargement par votre session, audité — aucun lien signé.">
      <InfoNote title="Avant tout export">
        {EXPORT_LIMITATION} {EXPORT_NOT_A_COMPLETE_ANSWER}
      </InfoNote>
      <ReadBlock state={state} label="Exports produits" onRetry={reload} skeletonHeight={96}>
        {(page) => (
          <>
            {page.items.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: 'tokens.textMuted' }}>
                Aucun export produit. Un export se déclenche depuis la fiche d’un compte.
              </Typography>
            ) : (
              <Box component="ul" aria-label="Exports récents" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {page.items.map((file) => (
                  <Box
                    component="li"
                    key={file.id}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      py: 1,
                      borderTop: 1,
                      borderColor: 'tokens.border',
                      '&:first-of-type': { borderTop: 0 },
                    }}
                  >
                    <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                      <Typography component="div" sx={{ fontSize: 14, fontWeight: 700, overflowWrap: 'anywhere' }}>
                        {file.userEmail} · {file.format.toUpperCase()}
                      </Typography>
                      <Typography component="div" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
                        {file.expired
                          ? `Expiré depuis le ${formatInstant(file.expiresAt)}`
                          : `Valable jusqu’au ${formatInstant(file.expiresAt)}`}
                      </Typography>
                    </Box>
                    <Badge label={file.expired ? 'Expiré' : 'Valide'} tone={file.expired ? 'neutral' : 'success'} />
                    {canDownload && !file.expired ? (
                      <Button
                        size="S"
                        icon={Download}
                        disabled={downloading !== null}
                        aria-label={`Télécharger l’export de ${file.userEmail}`}
                        onClick={() => {
                          void download(file);
                        }}
                      >
                        {downloading === file.id ? 'Téléchargement…' : 'Télécharger'}
                      </Button>
                    ) : null}
                  </Box>
                ))}
              </Box>
            )}
            {page.total > page.items.length ? (
              <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
                Les {String(page.items.length)} derniers exports sur {String(page.total)} conservés.
              </Typography>
            ) : null}
          </>
        )}
      </ReadBlock>
      {canDownload ? null : (
        <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
          Le téléchargement est réservé au rôle administrateur : votre rôle (support) lit la liste seulement.
        </Typography>
      )}
      {outcome === null ? null : (
        <Typography role={outcome.kind === 'error' ? 'alert' : 'status'} sx={{ fontSize: 13, color: outcome.kind === 'error' ? 'error.main' : 'tokens.successText' }}>
          {outcome.message}
        </Typography>
      )}
    </Card>
  );
};
